import React, { useState } from 'react';
import { Calendar, Clock, Users, FileText, Anchor, ShieldAlert, ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';

const ReviewMeetings = () => {
  const [expandedMeeting, setExpandedMeeting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const meetings = [
    {
      id: 1,
      title: "Quarterly Fleet Performance Review",
      date: "2025-03-25",
      time: "09:00 - 11:30",
      location: "Naval Command Center, Room 305",
      status: "upcoming",
      priority: "high",
      participants: [
        "Rear Admiral Johnson",
        "Fleet Commander Davis",
        "Engineering Chief Thompson",
        "Navigation Officer Rodriguez"
      ],
      agenda: [
        "Fleet operational efficiency review",
        "Maintenance schedule assessment",
        "Personnel rotation planning",
        "Resource allocation optimization"
      ],
      notes: "Bring updated maintenance logs and crew rotation schedules. Prepare for discussion on fuel efficiency improvements."
    },
    {
      id: 2,
      title: "USS Nimitz Systems Upgrade Review",
      date: "2025-03-22",
      time: "14:00 - 16:00",
      location: "Systems Integration Lab",
      status: "upcoming",
      priority: "medium",
      participants: [
        "Captain Wilson",
        "Systems Integration Lead Chen",
        "Technical Officer Patel",
        "Security Officer Washington"
      ],
      agenda: [
        "Navigation system upgrade status",
        "Communications array performance",
        "Cybersecurity implementation review",
        "Integration timeline assessment"
      ],
      notes: "Technical reports required from all department heads. Focus on timeline adjustments and resource requirements."
    },
    {
      id: 3,
      title: "Emergency Response Protocol Review",
      date: "2025-03-18",
      time: "10:00 - 12:00",
      location: "Crisis Management Center",
      status: "completed",
      priority: "high",
      participants: [
        "Safety Officer Williams",
        "Medical Director Dr. Lee",
        "Operations Chief Hernandez",
        "Training Coordinator Martinez"
      ],
      agenda: [
        "Storm response procedures",
        "Medical evacuation protocols",
        "Communications during emergencies",
        "Crew training assessment"
      ],
      notes: "All department heads successfully completed the emergency simulation. Recommendations for protocol updates to be submitted by end of week."
    },
    {
      id: 4,
      title: "Annual Budget Planning Meeting",
      date: "2025-03-15",
      time: "09:00 - 17:00",
      location: "Naval Headquarters, Conference Room A",
      status: "completed",
      priority: "high",
      participants: [
        "Budget Director Admiral Smith",
        "Fleet Financial Officer Parker",
        "Resource Allocation Specialist Kim",
        "Strategic Planning Officer Taylor"
      ],
      agenda: [
        "Next fiscal year budget review",
        "Resource allocation priorities",
        "Fleet maintenance funding",
        "Technology upgrade investments",
        "Personnel development programs"
      ],
      notes: "Budget proposals from all departments have been collected. Preliminary allocation plan will be presented for review."
    }
  ];

  const toggleMeeting = (id) => {
    setExpandedMeeting(expandedMeeting === id ? null : id);
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch = meeting.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || meeting.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Anchor className="mr-2 text-blue-600" size={24} />
              Naval Fleet Review Meetings
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Schedule, manage, and track all review meetings for the naval fleet
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors">
              Schedule New Meeting
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search meetings..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-500" size={18} />
              <select
                className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {filteredMeetings.length > 0 ? (
            filteredMeetings.map((meeting) => (
              <div key={meeting.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleMeeting(meeting.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-grow">
                      <div className="flex items-start">
                        <div className="mr-4">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <FileText className="text-blue-600" size={24} />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{meeting.title}</h3>
                          <div className="mt-1 flex flex-wrap gap-2">
                            <span className="inline-flex items-center text-xs font-medium">
                              <Calendar className="mr-1" size={14} />
                              {meeting.date}
                            </span>
                            <span className="inline-flex items-center text-xs font-medium">
                              <Clock className="mr-1" size={14} />
                              {meeting.time}
                            </span>
                            <span className="inline-flex items-center text-xs font-medium">
                              <Users className="mr-1" size={14} />
                              {meeting.participants.length} participants
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center mt-4 md:mt-0 space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(meeting.status)}`}>
                        {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(meeting.priority)}`}>
                        {meeting.priority.charAt(0).toUpperCase() + meeting.priority.slice(1)} Priority
                      </span>
                      {expandedMeeting === meeting.id ? (
                        <ChevronUp size={20} className="text-gray-500" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>
                
                {expandedMeeting === meeting.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                          <Users className="mr-2 text-gray-500" size={16} />
                          Participants
                        </h4>
                        <ul className="space-y-1 text-sm">
                          {meeting.participants.map((participant, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                              {participant}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                          <FileText className="mr-2 text-gray-500" size={16} />
                          Agenda
                        </h4>
                        <ol className="list-decimal list-inside space-y-1 text-sm">
                          {meeting.agenda.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ol>
                      </div>
                      <div className="md:col-span-2">
                        <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                          <ShieldAlert className="mr-2 text-gray-500" size={16} />
                          Notes & Instructions
                        </h4>
                        <p className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                          {meeting.notes}
                        </p>
                      </div>
                      <div className="md:col-span-2 flex flex-wrap gap-2">
                        <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors">
                          View Full Details
                        </button>
                        {meeting.status === 'upcoming' && (
                          <>
                            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors">
                              Edit Meeting
                            </button>
                            <button className="bg-red-100 text-red-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-200 transition-colors">
                              Cancel Meeting
                            </button>
                          </>
                        )}
                        {meeting.status === 'completed' && (
                          <button className="bg-green-100 text-green-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-green-200 transition-colors">
                            View Summary
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="flex justify-center mb-4">
                <Search className="text-gray-400" size={48} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No meetings found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewMeetings;